import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export function PageHero({ overline, title, sub, image, primary, secondary }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  return (
    <section ref={ref} data-testid="page-hero" className="relative h-[78vh] min-h-[560px] w-full overflow-hidden">
      <motion.img style={{ y }} src={image} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-noir via-noir/45 to-black/40" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-transparent" />
      <div className="relative z-10 h-full max-w-[1500px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-20 md:pb-24">
        <Reveal>
          {overline && <p className="font-body text-[11px] uppercase tracking-[0.45em] text-gold mb-5">{overline}</p>}
          <h1 className="font-serif font-light tracking-tighter leading-[0.92] text-[2.4rem] sm:text-6xl lg:text-7xl text-ink max-w-4xl">{title}</h1>
          {sub && <p className="font-body text-white/75 mt-6 max-w-2xl text-base md:text-lg leading-relaxed font-light">{sub}</p>}
          {(primary || secondary) && (
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              {primary && <Link to={primary.to} data-testid="hero-primary" className="inline-flex items-center justify-center border border-gold bg-gold text-black px-8 py-4 font-body text-xs uppercase tracking-[0.22em] hover:bg-transparent hover:text-gold transition-colors duration-300">{primary.label}</Link>}
              {secondary && <Link to={secondary.to} data-testid="hero-secondary" className="inline-flex items-center justify-center border border-white/40 text-ink px-8 py-4 font-body text-xs uppercase tracking-[0.22em] hover:border-gold hover:text-gold transition-colors duration-300">{secondary.label}</Link>}
            </div>
          )}
        </Reveal>
      </div>
    </section>
  );
}

export function SectionHeading({ overline, title, sub, center }) {
  return (
    <Reveal className={center ? "text-center" : ""}>
      {overline && <p className="font-body text-[11px] uppercase tracking-[0.4em] text-gold mb-5">{overline}</p>}
      <h2 className="font-serif text-3xl md:text-5xl font-light tracking-tight leading-[0.98]">{title}</h2>
      {sub && <p className={`font-body text-white/55 mt-6 text-sm md:text-base leading-relaxed font-light ${center ? "max-w-2xl mx-auto" : "max-w-2xl"}`}>{sub}</p>}
    </Reveal>
  );
}

export function FeatureGrid({ items, cols = 3 }) {
  const grid = cols === 2 ? "md:grid-cols-2" : "md:grid-cols-2 lg:grid-cols-3";
  return (
    <div className={`grid grid-cols-1 ${grid} gap-px bg-white/10 border border-white/10`}>
      {items.map((it, i) => {
        const Icon = it.icon;
        return (
          <Reveal key={it.title} delay={(i % 3) * 0.07}>
            <div data-testid={`feature-${i}`} className="group relative bg-noir p-8 md:p-10 h-full overflow-hidden">
              {it.img && (
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                  <img src={it.img} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-black/70" />
                </div>
              )}
              <div className="relative z-10">
                {Icon && <Icon size={24} className="text-gold mb-8" strokeWidth={1.2} />}
                <h3 className="font-serif text-2xl md:text-[26px] font-light mb-3">{it.title}</h3>
                <p className="font-body text-sm text-white/55 leading-relaxed font-light">{it.desc}</p>
                {it.to && <Link to={it.to} className="mt-5 inline-flex items-center gap-2 text-gold text-[11px] uppercase tracking-[0.2em] link-underline">{it.cta || "Learn More"} <ArrowRight size={13} /></Link>}
              </div>
            </div>
          </Reveal>
        );
      })}
    </div>
  );
}

export function StepList({ steps }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
      {steps.map((s, i) => (
        <Reveal key={s.title} delay={i * 0.08}>
          <div data-testid={`step-${i}`} className="bg-noir p-8 md:p-10 h-full">
            <span className="font-serif text-5xl text-gold/50">{String(i + 1).padStart(2, "0")}</span>
            <h3 className="font-serif text-2xl font-light mt-6 mb-3">{s.title}</h3>
            <p className="font-body text-sm text-white/55 leading-relaxed font-light">{s.desc}</p>
          </div>
        </Reveal>
      ))}
    </div>
  );
}

export function FAQAccordion({ items }) {
  return (
    <Accordion type="single" collapsible className="border-t border-white/10" data-testid="faq">
      {items.map((f, i) => (
        <AccordionItem key={i} value={`i${i}`} className="border-b border-white/10">
          <AccordionTrigger className="font-serif text-lg md:text-2xl font-light text-left py-6 hover:no-underline hover:text-gold [&[data-state=open]]:text-gold">{f.q}</AccordionTrigger>
          <AccordionContent className="font-body text-white/60 text-sm md:text-base leading-relaxed font-light pb-6">{f.a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

export function CTABand({ eyebrow, title, label = "START YOUR JOURNEY", to = "/start-your-journey", showRtb = false }) {
  return (
    <section data-testid="cta-band" className="py-24 md:py-32 px-6 md:px-12 bg-[#0a0a0a] border-y border-white/10">
      <div className="max-w-[1500px] mx-auto text-center">
        <Reveal>
          {eyebrow && <p className="font-body text-[11px] uppercase tracking-[0.4em] text-gold mb-5">{eyebrow}</p>}
          <h2 className="font-serif text-3xl md:text-5xl lg:text-6xl font-light tracking-tight max-w-3xl mx-auto leading-[0.98]">{title}</h2>
          <Link to={to} className="mt-10 inline-flex items-center gap-3 border border-gold text-gold px-10 py-4 font-body text-xs uppercase tracking-[0.22em] hover:bg-gold hover:text-black transition-colors duration-300">{label} <ArrowRight size={15} /></Link>
          {showRtb && (
            <p className="mt-7 font-body text-xs text-white/45 font-light">
              Already know what you want? <Link to="/ReadyToBook" data-testid="ctaband-rtb" className="text-gold link-underline">Ready to Book</Link>
            </p>
          )}
        </Reveal>
      </div>
    </section>
  );
}
